import { ImageDimensions } from '../../entities/file-validation.types';

/** `meta` sits right after `ftyp` in every HEIF writer seen; 64 KB covers a 48-tile grid's `iinf`/`iloc`. */
export const HEIF_HEAD_BYTES = 65536;

interface Box {
  readonly type: string;
  readonly start: number;
  readonly end: number;
}

function* boxes(view: DataView, start: number, end: number): Generator<Box> {
  let offset = start;
  while (offset + 8 <= end) {
    let size = view.getUint32(offset);
    const type = String.fromCharCode(...[4, 5, 6, 7].map(i => view.getUint8(offset + i)));
    let header = 8;
    if (size === 1) {
      if (offset + 16 > end) return;
      size = Number(view.getBigUint64(offset + 8));
      header = 16;
    } else if (size === 0) {
      size = end - offset;
    }

    if (size < header) return;
    yield { type, start: offset + header, end: Math.min(offset + size, end) };
    offset += size;
  }
}

const child = (view: DataView, parent: Box, type: string, skip = 0): Box | undefined =>
  [...boxes(view, parent.start + skip, parent.end)].find(box => box.type === type);

/** Size of the primary item as a viewer shows it: `ispe`, turned by `irot`. Null when the head does not hold it. */
export function readHeifDimensions(view: DataView): ImageDimensions | null {
  if (view.byteLength < 12 || view.getUint32(4) !== 0x66747970) return null;
  const top = [...boxes(view, 0, view.byteLength)];
  const meta = top.find(box => box.type === 'meta');
  if (!meta) return null;
  // meta, pitm and ipma are full boxes: version + 24-bit flags first.
  const pitm = child(view, meta, 'pitm', 4);
  const iprp = child(view, meta, 'iprp', 4);
  if (!pitm || !iprp) return null;
  const primary = view.getUint8(pitm.start) === 0 ? view.getUint16(pitm.start + 4) : view.getUint32(pitm.start + 4);
  const ipco = child(view, iprp, 'ipco');
  const ipma = child(view, iprp, 'ipma');
  if (!ipco || !ipma) return null;
  const properties = [...boxes(view, ipco.start, ipco.end)];

  const version = view.getUint8(ipma.start);
  const wideIndex = (view.getUint8(ipma.start + 3) & 1) === 1;
  let offset = ipma.start + 4;
  const entries = view.getUint32(offset);
  offset += 4;
  for (let entry = 0; entry < entries && offset < ipma.end; entry++) {
    const item = version < 1 ? view.getUint16(offset) : view.getUint32(offset);
    offset += version < 1 ? 2 : 4;
    const count = view.getUint8(offset++);
    const associated: Box[] = [];
    for (let i = 0; i < count; i++) {
      const index = wideIndex ? view.getUint16(offset) & 0x7fff : view.getUint8(offset) & 0x7f;
      offset += wideIndex ? 2 : 1;
      const property = properties[index - 1];
      if (property) associated.push(property);
    }

    if (item !== primary) continue;
    const ispe = associated.find(box => box.type === 'ispe');
    if (!ispe) return null;
    const width = view.getUint32(ispe.start + 4);
    const height = view.getUint32(ispe.start + 8);
    const irot = associated.find(box => box.type === 'irot');
    const quarterTurn = irot ? (view.getUint8(irot.start) & 3) % 2 === 1 : false;
    return quarterTurn ? { width: height, height: width } : { width, height };
  }

  return null;
}
